import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, ResolveFn, Router, RouterModule } from '@angular/router';
import { AppPageModule } from '@vsolv/vectors-ui/app-page';
import { catchError, filter, first, map, of, shareReplay } from 'rxjs';
import { JsonPreviewComponent } from './json-preview.component';

export interface IEDIItem {
  id: string;

  created: Date;
  modified: Date;

  type: 'provision' | 'update' | 'cancel' | 'claim' | 'mixed';
  status: 'processing' | 'success' | 'error';

  source_file: string;
  source_file_row: number;

  source_data: object;
  command_data: object;
  command_response: object | null;
}

export const EDIItemPageResolver: ResolveFn<IEDIItem | null> = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);

  const source_file = route.paramMap.get('source_file');
  if (!source_file) return (router.navigate(['/']), null);

  const id = route.paramMap.get('id');
  if (!id) return (router.navigate(['/']), null);

  const item = router.getCurrentNavigation()?.extras.state as IEDIItem | null;
  if (item?.id === id) return item;

  return inject(HttpClient)
    .get<IEDIItem>(`/v1/edi/items/${source_file}/${id}`)
    .pipe(
      catchError(() => (router.navigate(['/']), of(null))),
      first()
    );
};

@Component({
  standalone: true,
  templateUrl: './item.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, JsonPreviewComponent, RouterModule, AppPageModule],
})
export class EDIItemPage {
  private route = inject(ActivatedRoute);

  protected item$ = this.route.data.pipe(
    map(data => data['item'] as IEDIItem),
    filter(Boolean),
    shareReplay(1)
  );

  protected pageConfig$ = this.item$.pipe(
    map(item => {
      const filename = decodeURIComponent(item.source_file)
        .replace(/.*\//g, '')
        .replace(/^(?:\d{13}_)+/g, '');

      return {
        isSubPage: true,
        title: filename + ' › Row ' + item.source_file_row,
        description: 'Review details of this specific item.',
      };
    })
  );

  protected sourceData$ = this.item$.pipe(
    map(item => {
      return {
        data: item.source_data,
        title: `Source data: <span class="text-gray-700 font-normal">${item.source_file} (row ${item.source_file_row})</span>`,
      };
    })
  );

  protected commandData$ = this.item$.pipe(
    map(item => {
      return {
        data: item.command_data,
        title: `Command: <span class="text-gray-700 font-normal">${
          {
            provision: 'ProvisionWarrantyCommand',
            update: 'UpdateWarrantyCommand',
            cancel: 'CancelWarrantyCommand',
            claim: 'OpenClaimCommand',
            mixed: 'ProvisionWarrantyCommand / CancelWarrantyCommand',
          }[item.type]
        }</span>`,
      };
    })
  );

  protected resultData$ = this.item$.pipe(
    map(item => {
      if (!item.command_response) return null;
      return {
        data: item.command_response,
        title: `Result: <span class="text-gray-700 font-normal">${item.status}</span>`,
      };
    })
  );
}
